/**
 * Citation metadata from backend (nested in metadata field)
 */
export interface CitationMetadata {
  /** Source service identifier */
  source_service?: "google_drive" | "sharepoint" | "confluence" | "files_hub";
  /**
   * Generic source identifier (Confluence page id, SharePoint item id, etc.).
   * Reserved for future use; not read by any viewer yet.
   */
  source_file_id?: string;
  /** Generic external URL for the source document (Confluence page URL, SharePoint web link, etc.) */
  source_web_link?: string;
  /**
   * Generic last-modified timestamp from the source system.
   * Reserved for future use; not read by any viewer yet.
   */
  source_modified_time?: string;
  /** Google Drive file ID */
  google_drive_file_id?: string;
  /** Google Drive web view link */
  google_drive_web_view_link?: string;
  /** Original MIME type */
  mimeType?: string;
  /** File name from metadata */
  name?: string;
  /**
   * Where the cited passage sits, measured at ingestion. Typed as unknown on
   * purpose, because it arrives off a search index written by a backend that
   * may be older or newer than this build. Read it through `pdfBoxesFor` in
   * `@/utils/citationAnchor`, which validates the shape and yields undefined
   * for anything it does not recognise.
   */
  citation_anchor?: unknown;
  [key: string]: unknown; // Allow additional metadata fields
}

/**
 * One rectangle of a stored pdf citation anchor, in PDF points with a top-left
 * origin, unscaled, as the ingest measures it.
 *
 * The ingest parses with pdfminer, which applies the page's rotation and zeroes
 * the MediaBox origin before any glyph is placed, and the layout code flips to a
 * top-left origin once. The viewer's overlay is sized from the pdf.js viewport,
 * which applies the same rotation but takes its size from the CropBox, so the
 * two agree on every page whose CropBox is the MediaBox. A page cropped to an
 * offset origin shifts the highlight by that offset, a limitation shared with
 * the text-matching path, which the viewer warns about when it loads such a page.
 */
export interface PdfCitationBox {
  /** 1-indexed, as a reader counts pages */
  page: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Citation data structure from SSE message_completed events
 */
export interface Citation {
  /** Normalized label (e.g., "C1", "C2") */
  label: string;

  /** Document ID for downloading the original file */
  document_id: string;

  /** Display filename */
  filename: string;

  /** Full citation text from the document */
  text: string;

  /** Short preview snippet for display in lists */
  snippet: string;

  /** Optional evidence ID metadata */
  evidence_id?: string;

  /** Optional chunk index metadata */
  chunk_index?: number;

  /** Optional relevance score metadata */
  score?: number;

  /** Optional metadata from backend (contains Google Drive info, etc.) */
  metadata?: CitationMetadata;
}

/**
 * Helper to get Google Drive web view link from citation
 */
export function getGoogleDriveWebViewLink(citation: Citation | null): string | undefined {
  return citation?.metadata?.google_drive_web_view_link;
}

/**
 * Helper to check if citation is from Google Drive
 */
export function isGoogleDriveCitation(citation: Citation | null): boolean {
  return (
    citation?.metadata?.source_service === "google_drive" ||
    !!citation?.metadata?.google_drive_file_id ||
    !!citation?.metadata?.google_drive_web_view_link
  );
}

/**
 * Helper to check if citation is from Confluence. Detection relies solely on the
 * backend setting metadata.source_service correctly; unlike Google Drive there is
 * no id or link heuristic fallback.
 */
export function isConfluenceCitation(citation: Citation | null): boolean {
  return citation?.metadata?.source_service === "confluence";
}

/**
 * Helper to check if citation is from SharePoint. Like isConfluenceCitation, this
 * depends entirely on the backend setting metadata.source_service correctly.
 */
export function isSharePointCitation(citation: Citation | null): boolean {
  return citation?.metadata?.source_service === "sharepoint";
}

/**
 * Returns an external web link for a citation's source document. Prefers the
 * generic source_web_link (used by Confluence and SharePoint) and falls back to
 * the legacy Google Drive field for older Google Drive citations.
 */
export function getSourceWebLink(citation: Citation | null): string | undefined {
  return citation?.metadata?.source_web_link ?? citation?.metadata?.google_drive_web_view_link;
}

/**
 * Extended chat message type that includes citations
 * This extends the base ChatMessage from @webuild-ai/admin
 */
export interface ChatMessageWithCitations {
  citations?: Citation[];
}
